class CuratedShopTheLook extends HTMLElement {
  constructor() {
    super();

    this.activeLook = this.dataset.initialLook || 'resort';
    this.products = [...this.querySelectorAll('[data-product]')];
    this.tabs = [...this.querySelectorAll('[data-look]')];
    this.grid = this.querySelector('.curated-look__grid');
    this.button = this.querySelector('[data-add-look]');
    this.message = this.querySelector('[data-message]');
    this.miniCart = document.querySelector('mini-cart');

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectLook(tab.dataset.look));
      tab.addEventListener('keydown', (event) => this.handleTabKeydown(event));
    });
    this.products.forEach((product) => {
      product.querySelector('[data-product-toggle]').addEventListener('change', () => this.selectCustomLook());
      product.querySelector('[data-variant-select]').addEventListener('change', () => {
        this.updateProductPrice(product);
        this.updateSummary();
      });
      product.querySelectorAll('[data-product-link]').forEach((link) => {
        link.addEventListener('click', () => this.trackProductClick(product));
      });
    });
    if (this.button) this.button.addEventListener('click', () => this.addSelected());

    this.selectLook(this.activeLook, false);
    this.observeView();
  }

  handleTabKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    const lastIndex = this.tabs.length - 1;
    const destinations = {
      ArrowRight: currentIndex === lastIndex ? 0 : currentIndex + 1,
      ArrowLeft: currentIndex === 0 ? lastIndex : currentIndex - 1,
      Home: 0,
      End: lastIndex
    };
    if (!(event.key in destinations)) return;

    event.preventDefault();
    const nextTab = this.tabs[destinations[event.key]];
    nextTab.focus();
    this.selectLook(nextTab.dataset.look);
  }

  selectLook(look, track = true) {
    this.activeLook = look;
    this.tabs.forEach((tab) => {
      const active = tab.dataset.look === look;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active.toString());
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    if (this.grid) {
      const activeTab = this.tabs.find((tab) => tab.dataset.look === look);
      this.grid.setAttribute('aria-labelledby', activeTab.id);
    }

    if (look === 'custom') {
      this.products.forEach((product) => {
        product.querySelector('[data-product-toggle]').checked = false;
        product.classList.remove('is-selected');
      });
    } else {
      this.products.forEach((product) => {
        const selected = product.dataset[look] === 'true';
        product.querySelector('[data-product-toggle]').checked = selected;
        product.classList.toggle('is-selected', selected);
      });
    }

    this.hideMessage();
    this.updateSummary();
    if (track) {
      this.scrollSelectedIntoView();
      this.pushEvent('shop_the_look_select', { look_name: this.lookName });
    }
  }

  scrollSelectedIntoView() {
    if (!this.grid || this.grid.scrollWidth <= this.grid.clientWidth) return;
    const selectedProduct = this.products.find((product) => product.querySelector('[data-product-toggle]').checked);
    if (!selectedProduct) return;

    const gridRect = this.grid.getBoundingClientRect();
    const productRect = selectedProduct.getBoundingClientRect();
    const left = this.grid.scrollLeft
      + productRect.left
      - gridRect.left
      - (this.grid.clientWidth - productRect.width) / 2;
    this.grid.scrollTo({ left: Math.max(0, Math.round(left)), behavior: 'smooth' });
  }

  selectCustomLook() {
    const wasCustom = this.activeLook === 'custom';
    this.activeLook = 'custom';
    this.tabs.forEach((tab) => {
      const active = tab.dataset.look === 'custom';
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active.toString());
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });
    if (this.grid) {
      const customTab = this.tabs.find((tab) => tab.dataset.look === 'custom');
      this.grid.setAttribute('aria-labelledby', customTab.id);
    }
    this.products.forEach((product) => {
      product.classList.toggle('is-selected', product.querySelector('[data-product-toggle]').checked);
    });
    this.hideMessage();
    this.updateSummary();
    if (!wasCustom) this.pushEvent('shop_the_look_select', { look_name: this.lookName });
  }

  get lookName() {
    return this.tabs.find((tab) => tab.dataset.look === this.activeLook)?.textContent.trim() || this.activeLook;
  }

  get selectedItems() {
    return this.products
      .map((product, index) => {
        if (!product.querySelector('[data-product-toggle]').checked) return null;
        const variant = product.querySelector('[data-variant-select]');
        const option = variant.selectedOptions ? variant.selectedOptions[0] : variant;
        return {
          id: variant.value,
          quantity: 1,
          price: Number(option.dataset.price),
          productId: product.dataset.productId,
          handle: product.dataset.productHandle,
          title: product.dataset.productTitle,
          variantTitle: option.dataset.variantTitle,
          index: index + 1
        };
      })
      .filter(Boolean);
  }

  updateSummary() {
    const items = this.selectedItems;
    const value = items.reduce((sum, item) => sum + item.price, 0);
    const count = this.querySelector('[data-selection-count]');
    const total = this.querySelector('[data-selection-total]');

    if (count) count.textContent = `${items.length} ${items.length === 1 ? 'item' : 'items'}`;
    if (total) total.textContent = this.formatMoney(value);
    if (this.button) this.button.disabled = items.length === 0;
  }

  updateProductPrice(product) {
    const variant = product.querySelector('[data-variant-select]');
    const option = variant.selectedOptions ? variant.selectedOptions[0] : variant;
    product.querySelector('[data-product-price]').textContent = this.formatMoney(Number(option.dataset.price));
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      style: 'currency',
      currency: this.dataset.currency
    }).format(cents / 100);
  }

  async addSelected() {
    const items = this.selectedItems;
    if (!items.length || this.button.classList.contains('loading')) return;

    const value = items.reduce((sum, item) => sum + item.price, 0);
    const analyticsItems = this.analyticsItems(items);
    this.button.disabled = true;
    this.button.classList.add('loading');
    this.hideMessage();

    this.pushEvent('shop_the_look_add', {
      look_name: this.lookName,
      item_count: items.length,
      multi_item: items.length > 1,
      value: value / 100,
      currency: this.dataset.currency,
      items: analyticsItems
    });

    const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.id) : [];
    const body = {
      items: items.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        properties: {
          _shop_the_look: this.lookName,
          _shop_the_look_section: this.dataset.sectionId
        }
      }))
    };

    try {
      const response = await fetch(window.routes.cart_add_url, {
        ...fetchConfig('javascript'),
        body: JSON.stringify(body)
      });
      let result;
      try {
        result = await response.json();
      } catch (error) {
        throw new Error(window.cartStrings.error);
      }
      if (!response.ok || result.status) throw new Error(result.description || result.message || window.cartStrings.error);

      this.pushEvent('shop_the_look_conversion', {
        look_name: this.lookName,
        item_count: items.length,
        multi_item: items.length > 1,
        value: value / 100,
        currency: this.dataset.currency,
        items: analyticsItems
      });
      this.pushEvent('add_to_cart', {
        shop_the_look: this.lookName,
        ecommerce: {
          currency: this.dataset.currency,
          value: value / 100,
          items: analyticsItems
        }
      });

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'curated-shop-the-look' });
      }

      if (this.miniCart && sections.length) {
        try {
          const sectionUrl = `${window.routes.root_url}?sections=${encodeURIComponent(sections.join(','))}`;
          const sectionResponse = await fetch(sectionUrl);
          const renderedSections = await sectionResponse.json();
          const missingSection = !sectionResponse.ok || sections.some((section) => !renderedSections[section]);
          if (missingSection) throw new Error('Cart sections were unavailable');
          this.miniCart.renderContents({ id: items[0].id, sections: renderedSections });
        } catch (error) {
          window.location.assign(window.routes.cart_url);
        }
      } else {
        window.location.assign(window.routes.cart_url);
      }
    } catch (error) {
      this.message.textContent = error.message || window.cartStrings.error;
      this.message.hidden = false;
    } finally {
      this.button.classList.remove('loading');
      this.button.disabled = this.selectedItems.length === 0;
    }
  }

  analyticsItems(items) {
    return items.map((item) => ({
      item_id: item.productId,
      item_name: item.title,
      item_variant: item.variantTitle,
      item_list_id: `shop-the-look-${this.dataset.sectionId}`,
      item_list_name: this.lookName,
      price: item.price / 100,
      quantity: 1,
      index: item.index
    }));
  }

  trackProductClick(product) {
    const variant = product.querySelector('[data-variant-select]');
    const option = variant.selectedOptions ? variant.selectedOptions[0] : variant;
    const item = {
      id: variant.value,
      productId: product.dataset.productId,
      title: product.dataset.productTitle,
      variantTitle: option.dataset.variantTitle,
      price: Number(option.dataset.price),
      index: this.products.indexOf(product) + 1
    };
    this.pushEvent('shop_the_look_product_click', {
      look_name: this.lookName,
      items: this.analyticsItems([item])
    });
  }

  observeView() {
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      const items = this.products.map((product, index) => {
        const variant = product.querySelector('[data-variant-select]');
        const option = variant.selectedOptions ? variant.selectedOptions[0] : variant;
        return {
          productId: product.dataset.productId,
          title: product.dataset.productTitle,
          variantTitle: option.dataset.variantTitle,
          price: Number(option.dataset.price),
          index: index + 1
        };
      });
      this.pushEvent('shop_the_look_view', {
        look_name: this.lookName,
        item_count: items.length,
        items: this.analyticsItems(items)
      });
    }, { threshold: 0.25 });
    observer.observe(this);
  }

  pushEvent(event, detail) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, shop_the_look_id: this.dataset.sectionId, ...detail });
  }

  hideMessage() {
    if (!this.message) return;
    this.message.hidden = true;
    this.message.textContent = '';
  }
}

if (!customElements.get('curated-shop-the-look')) {
  customElements.define('curated-shop-the-look', CuratedShopTheLook);
}
